import React, { useEffect, useState } from 'react';
import { logger } from 'src/commons/utils/logger';
import { requestWithRetry } from 'src/commons/utils/asyncRequest';
import {
  PopoverMultipleLines,
  PopoverWithContent,
  TextLinkPopover,
  TextLinkPopoverRemote,
} from '../types';
import {
  DynamicPopover,
  isValidPopoverResponse,
  MultipleLines,
} from '../utils';
import { TextButtonPopoverAttribute } from '@amzn/storm-ui-page-header';

export const ajaxGetPopoverResponse = async (
  url: string,
): Promise<PopoverMultipleLines | PopoverWithContent | string | null> => {
  try {
    /*
     * Read as text and parse by hand, rather than letting the request layer call
     * res.json(). This endpoint legitimately answers with EITHER a popover
     * object or a bare string, and axios's default transformResponse gave the
     * raw string back whenever JSON.parse threw. Asking for 'json' would turn
     * the plain-string response into a parse failure and this function would
     * return null where it used to return the text.
     */
    const body = await requestWithRetry<string>({
      url,
      responseType: 'text',
    });
    if (!body) return null;
    let data: unknown = body;
    try {
      data = JSON.parse(body);
    } catch {
      // Not JSON: the endpoint sent plain text, which is a valid response here.
    }
    if (!data) return null;
    if (isValidPopoverResponse(data))
      return data as PopoverMultipleLines | PopoverWithContent;
    return data as PopoverMultipleLines | PopoverWithContent | string;
  } catch (error: any) {
    logger.info(
      `asyncRequests - textLinkPopoverRemote get request for endpoint ${url}. error: ${error}`,
    );
    return null;
  }
};

export const AttributesTextLinkPopover: React.VFC<
  TextLinkPopoverRemote | TextLinkPopover
> = (props) => {
  const { id, label, content, ariaLabel, tooltipPosition, withCloseButton } =
    props;
  const { popoverContent } = props as TextLinkPopover;
  const { popoverAPI, apiCallFailureMessage } = props as TextLinkPopoverRemote;
  const [tooltipContent, setTooltipContent] = useState<any>(null);

  useEffect(() => {
    if (popoverAPI) {
      const fetchPopoverContent = async () => {
        const response = await ajaxGetPopoverResponse(popoverAPI);
        setTooltipContent(response);
      };
      fetchPopoverContent();
    }
  }, []);

  if (!popoverContent && (!popoverAPI || !apiCallFailureMessage)) return null;

  const TooltipContent = () =>
    popoverContent ? (
      <MultipleLines lines={popoverContent} />
    ) : (
      <DynamicPopover popover={tooltipContent ?? apiCallFailureMessage} />
    );

  return (
    <TextButtonPopoverAttribute
      id={id}
      prefixLabel={label}
      content={content}
      popoverContent={<TooltipContent />}
      popoverPosition={tooltipPosition}
      withCloseButton={withCloseButton}
      aria-label={ariaLabel}
      data-ccx-e2e-id={`aac-attribute-${id}`}
    />
  );
};
